/**
 * Generatore di schemi colore Material 3 a partire da un colore "seme".
 * Nessuna dipendenza esterna. Serve per i preset e per il colore personalizzato; il colore
 * di default (Equal, #5B3AA8) NON passa da qui: usa i valori statici di styles/tokens.css.
 *
 * Metodo (brief §2.1): il seme viene convertito in OKLCH; da questo si ricavano le palette
 * tonali P (primaria), S (secondaria), T (terziaria), N (neutra), NV (neutra variante).
 * Ogni "tono" T è una luminosità percettiva: qui la luminanza relativa (Y) del colore è quella
 * della scala CIE L* = T, cioè lo stesso significato dei toni di Material (HCT). Così le
 * differenze di tono garantiscono il contrasto come in M3 (50 punti ≈ 4,5:1) e non serve
 * correggere nulla dopo. Il croma viene ridotto (non troncato) quando il colore esce da sRGB,
 * per non spostare la tinta.
 *
 * Il risultato è nello stesso formato dei token: triplette HSL "H S% L%".
 */

export type SchemeRoles = Record<string, string>
export interface Scheme { light: SchemeRoles; dark: SchemeRoles }

type Vec3 = readonly [number, number, number]

const HEX_RE = /^#[0-9a-fA-F]{6}$/

export function isHexColor(v: string): boolean {
  return HEX_RE.test(v)
}

/** Normalizza in "#RRGGBB" maiuscolo; restituisce null se non è un colore esadecimale a 6 cifre. */
export function normalizeHex(v: string): string | null {
  const t = v.trim()
  return HEX_RE.test(t) ? t.toUpperCase() : null
}

/* ── Conversioni di colore ── */

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}
function linearToSrgb(c: number): number {
  return c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055
}

function hexToLinear(hex: string): Vec3 {
  const n = parseInt(hex.slice(1), 16)
  return [srgbToLinear(((n >> 16) & 255) / 255), srgbToLinear(((n >> 8) & 255) / 255), srgbToLinear((n & 255) / 255)]
}

function linearToOklab([r, g, b]: Vec3): Vec3 {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ]
}

function oklabToLinear([L, a, b]: Vec3): Vec3 {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ]
}

function oklchToLinear(L: number, C: number, hDeg: number): Vec3 {
  const h = (hDeg * Math.PI) / 180
  return oklabToLinear([L, C * Math.cos(h), C * Math.sin(h)])
}

function inGamut([r, g, b]: Vec3): boolean {
  const e = 1e-4
  return r >= -e && r <= 1 + e && g >= -e && g <= 1 + e && b >= -e && b <= 1 + e
}

const clamp01 = (x: number) => Math.min(1, Math.max(0, x))
const luminance = ([r, g, b]: Vec3) => 0.2126 * clamp01(r) + 0.7152 * clamp01(g) + 0.0722 * clamp01(b)

function toByte(c: number): number {
  return Math.round(clamp01(linearToSrgb(clamp01(c))) * 255)
}

function linearToHex(rgb: Vec3): string {
  return '#' + rgb.map(c => toByte(c).toString(16).padStart(2, '0')).join('').toUpperCase()
}

/** Colore a luminosità L con il massimo croma ≤ C che sta in sRGB. */
function fitChroma(L: number, C: number, h: number): Vec3 {
  const full = oklchToLinear(L, C, h)
  if (inGamut(full)) return full
  let lo = 0
  let hi = C
  for (let i = 0; i < 22; i++) {
    const mid = (lo + hi) / 2
    if (inGamut(oklchToLinear(L, mid, h))) lo = mid
    else hi = mid
  }
  return oklchToLinear(L, lo, h)
}

/** Luminanza relativa (Y) corrispondente a un tono CIE L* (0-100). */
function toneToY(tone: number): number {
  return tone <= 8 ? tone / 903.2962963 : ((tone + 16) / 116) ** 3
}

/** Colore (hex) con tinta h, croma massimo C e tono T. */
export function toneColor(h: number, C: number, tone: number): string {
  if (tone <= 0) return '#000000'
  if (tone >= 100) return '#FFFFFF'
  const target = toneToY(tone)
  let lo = 0
  let hi = 1
  let best: Vec3 = [0, 0, 0]
  for (let i = 0; i < 36; i++) {
    const L = (lo + hi) / 2
    const rgb = fitChroma(L, C, h)
    best = rgb
    if (luminance(rgb) < target) lo = L
    else hi = L
  }
  return linearToHex(best)
}

/* ── HSL / tripletta ── */

export function hexToHsl(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16)
  const r = ((n >> 16) & 255) / 255
  const g = ((n >> 8) & 255) / 255
  const b = (n & 255) / 255
  const mx = Math.max(r, g, b)
  const mn = Math.min(r, g, b)
  const d = mx - mn
  const l = (mx + mn) / 2
  let h = 0
  let s = 0
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn)
    if (mx === r) h = (g - b) / d + (g < b ? 6 : 0)
    else if (mx === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h *= 60
  }
  return [h, s * 100, l * 100]
}

export function hslToHex(h: number, s: number, l: number): string {
  const sn = s / 100
  const ln = l / 100
  const a = sn * Math.min(ln, 1 - ln)
  const f = (n: number) => {
    const k = (n + h / 30) % 12
    return ln - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))
  }
  const byte = (v: number) => Math.round(clamp01(v) * 255).toString(16).padStart(2, '0')
  return ('#' + byte(f(0)) + byte(f(8)) + byte(f(4))).toUpperCase()
}

const r1 = (x: number) => Math.round(x * 10) / 10

/** "#RRGGBB" -> "H S% L%" (formato dei token --md-*). */
export function hexToTriplet(hex: string): string {
  const [h, s, l] = hexToHsl(hex)
  return `${r1(h)} ${r1(s)}% ${r1(l)}%`
}

/** "H S% L%" -> "r, g, b" (formato delle variabili --bs-*-rgb di Bootstrap). */
export function tripletToRgbList(t: string): string {
  const m = /^([\d.]+) ([\d.]+)% ([\d.]+)%$/.exec(t)
  if (!m) return '0, 0, 0'
  const hex = hslToHex(Number(m[1]), Number(m[2]), Number(m[3]))
  const n = parseInt(hex.slice(1), 16)
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`
}

/* ── Schema ── */

interface Tonal { h: number; c: number }
const tone = (p: Tonal, t: number) => hexToTriplet(toneColor(p.h, p.c, t))

/** Nomi dei ruoli generati (senza il prefisso "--md-"). Tutti gli altri restano statici. */
export const SCHEME_ROLE_NAMES = [
  'primary', 'on-primary', 'primary-container', 'on-primary-container',
  'secondary', 'on-secondary', 'secondary-container', 'on-secondary-container',
  'tertiary', 'on-tertiary', 'tertiary-container', 'on-tertiary-container',
  'surface', 'surface-dim', 'surface-bright',
  'surface-container-lowest', 'surface-container-low', 'surface-container',
  'surface-container-high', 'surface-container-highest',
  'on-surface', 'on-surface-variant', 'outline', 'outline-variant',
  'inverse-surface', 'inverse-on-surface', 'inverse-primary',
] as const

/**
 * Schema chiaro e scuro per un seme esadecimale. Error, success, warning e info non derivano
 * dal seme (restano quelli di tokens.css).
 */
export function generateScheme(seedHex: string): Scheme {
  const hex = normalizeHex(seedHex) ?? '#5B3AA8'
  const [, a, b] = linearToOklab(hexToLinear(hex))
  const chroma = Math.hypot(a, b)
  const hue = (Math.atan2(b, a) * 180) / Math.PI
  const hueDeg = (hue + 360) % 360
  // Il minimo di croma 0,12 evita palette spente per semi poco saturi; un seme quasi grigio
  // (croma < 0,04, come il preset "Grigio") resta però grigio invece di diventare colorato.
  const base = chroma < 0.04 ? chroma : Math.max(chroma, 0.12)
  const P: Tonal = { h: hueDeg, c: base }
  const S: Tonal = { h: hueDeg, c: base / 3 }
  const T: Tonal = { h: (hueDeg + 60) % 360, c: base / 2 }
  const N: Tonal = { h: hueDeg, c: Math.min(0.012, base) }
  const NV: Tonal = { h: hueDeg, c: Math.min(0.024, base) }

  const light: SchemeRoles = {
    'primary': tone(P, 40), 'on-primary': tone(P, 100), 'primary-container': tone(P, 90), 'on-primary-container': tone(P, 10),
    'secondary': tone(S, 40), 'on-secondary': tone(S, 100), 'secondary-container': tone(S, 90), 'on-secondary-container': tone(S, 10),
    'tertiary': tone(T, 40), 'on-tertiary': tone(T, 100), 'tertiary-container': tone(T, 90), 'on-tertiary-container': tone(T, 10),
    'surface': tone(N, 98), 'surface-dim': tone(N, 87), 'surface-bright': tone(N, 98),
    'surface-container-lowest': tone(N, 100), 'surface-container-low': tone(N, 96), 'surface-container': tone(N, 94),
    'surface-container-high': tone(N, 92), 'surface-container-highest': tone(N, 90),
    'on-surface': tone(N, 10), 'on-surface-variant': tone(NV, 30), 'outline': tone(NV, 50), 'outline-variant': tone(NV, 80),
    'inverse-surface': tone(N, 20), 'inverse-on-surface': tone(N, 95), 'inverse-primary': tone(P, 80),
  }
  const dark: SchemeRoles = {
    'primary': tone(P, 80), 'on-primary': tone(P, 20), 'primary-container': tone(P, 30), 'on-primary-container': tone(P, 90),
    'secondary': tone(S, 80), 'on-secondary': tone(S, 20), 'secondary-container': tone(S, 30), 'on-secondary-container': tone(S, 90),
    'tertiary': tone(T, 80), 'on-tertiary': tone(T, 20), 'tertiary-container': tone(T, 30), 'on-tertiary-container': tone(T, 90),
    'surface': tone(N, 6), 'surface-dim': tone(N, 6), 'surface-bright': tone(N, 24),
    'surface-container-lowest': tone(N, 4), 'surface-container-low': tone(N, 10), 'surface-container': tone(N, 12),
    'surface-container-high': tone(N, 17), 'surface-container-highest': tone(N, 22),
    'on-surface': tone(N, 90), 'on-surface-variant': tone(NV, 80), 'outline': tone(NV, 60), 'outline-variant': tone(NV, 30),
    'inverse-surface': tone(N, 90), 'inverse-on-surface': tone(N, 20), 'inverse-primary': tone(P, 40),
  }
  return { light, dark }
}
